/**
 * takeTrip.ts — copy someone else's trip into your own planner.
 *
 * The explorer restores an unsaved trip from sessionStorage on load
 * (HomeClient: TRIP_DRAFT_KEY, then the device-wide TRIP_DRAFT_SHARED_KEY
 * for ?plan=1 links). "Take this trip" writes the shared itinerary into
 * both, marks it as taken, and routes to /?plan=1. The copy is a fresh
 * draft: new stop ids, no trip id, no "booked" ticks (those were the other
 * person's bookings), and the start date only if it's still ahead.
 *
 * Keys and draft shape must stay in step with HomeClient.tsx.
 */
import type { TripStop } from '../../types/trip';
import { BOOKED_TAG } from '../../utils/tripSheet';
import { photoTitles } from './stopPhotos';

export const TRIP_DRAFT_KEY = 'tripDraftInProgress';
export const TRIP_DRAFT_SHARED_KEY = 'trip-planner.draftTrip';
/** Set alongside the draft so the planner can say where the trip came from. */
export const TAKEN_TRIP_KEY = 'tp-taken-trip';
/** The shared page's hero, so the planner opens on the same picture. */
export const TAKEN_COVER_KEY = 'tp-taken-cover';
/** The share token a taken trip came from (the planner's "Shared trip" breadcrumb links back). */
export const TAKEN_FROM_KEY = 'tp-taken-from';
/** Same cache the planner's stop thumbnails read (planner/stopPhotos). */
export const STOP_PHOTO_STORE_KEY = 'tp-stop-photos-v1';
/** Tokens this browser has already counted a take for. */
export const TAKEN_TOKENS_KEY = 'tp-taken-tokens';

export interface TakeTripSource {
  name?: string | null;
  start_date?: string | null;
  drive_hours_per_day?: number | null;
  vehicle_type?: string | null;
}

export interface PlannerDraft {
  tripStops: TripStop[];
  routeName: string;
  startPoint: string;
  endPoint: string;
  tripStartDate: string | null;
  maxDailyDriveHours?: number;
  tripVehicleType?: string;
  activeTripId: null;
}

const VEHICLES = new Set(['car', 'sedan', '4wd', 'caravan', 'motorhome', 'motorcycle']);

function todayIso(now: Date): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

let counter = 0;
export function freshStopId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  counter += 1;
  return `trip-${Date.now()}-${counter}-${Math.random().toString(16).slice(2)}`;
}

export function buildTakenDraft(
  trip: TakeTripSource,
  stops: TripStop[],
  { now = new Date(), newId = freshStopId }: { now?: Date; newId?: () => string } = {}
): PlannerDraft | null {
  const usable = stops.filter((s) => (s.name || '').trim() || (typeof s.lat === 'number' && typeof s.lng === 'number'));
  if (usable.length < 2) return null;
  const tripStops: TripStop[] = usable.map((s) => {
    const tags = (s.tags || []).filter((t) => t !== BOOKED_TAG);
    const copy: TripStop = {
      id: newId(),
      name: s.name,
      kind: s.kind,
      ...(typeof s.entityId === 'number' ? { entityId: s.entityId } : {}),
      ...(typeof s.lat === 'number' ? { lat: s.lat } : {}),
      ...(typeof s.lng === 'number' ? { lng: s.lng } : {}),
      ...(typeof s.nights === 'number' ? { nights: s.nights } : {}),
      ...(tags.length ? { tags } : {}),
      ...(s.notes ? { notes: s.notes } : {}),
    };
    return copy;
  });
  const start = trip.start_date && /^\d{4}-\d{2}-\d{2}$/.test(trip.start_date) ? trip.start_date : null;
  const vehicle = (trip.vehicle_type || '').toLowerCase();
  return {
    tripStops,
    routeName: (trip.name || '').trim() || `${tripStops[0].name} to ${tripStops[tripStops.length - 1].name}`,
    startPoint: tripStops[0].name,
    endPoint: tripStops[tripStops.length - 1].name,
    tripStartDate: start && start >= todayIso(now) ? start : null,
    ...(typeof trip.drive_hours_per_day === 'number' && trip.drive_hours_per_day > 0
      ? { maxDailyDriveHours: trip.drive_hours_per_day }
      : {}),
    ...(VEHICLES.has(vehicle) ? { tripVehicleType: vehicle } : {}),
    activeTripId: null,
  };
}

/** An unsaved trip already in this tab's planner (the one taking would replace). */
export function readUnsavedDraft(storage: Pick<Storage, 'getItem'> | null): { name: string; stops: number } | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(TRIP_DRAFT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    const n = Array.isArray(d?.tripStops) ? d.tripStops.length : 0;
    if (n < 2 || d?.activeTripId) return null;
    return { name: String(d.routeName || '').trim() || 'your trip', stops: n };
  } catch {
    return null;
  }
}

/** Write the draft where the planner restores from. False if storage is unavailable. */
export function writeTakenDraft(
  draft: PlannerDraft,
  session: Pick<Storage, 'setItem'> | null,
  local: Pick<Storage, 'setItem'> | null,
  now = Date.now()
): boolean {
  let ok = false;
  try {
    session?.setItem(TRIP_DRAFT_KEY, JSON.stringify(draft));
    session?.setItem(TAKEN_TRIP_KEY, draft.routeName);
    ok = !!session;
  } catch {
    /* private mode / quota */
  }
  try {
    local?.setItem(TRIP_DRAFT_SHARED_KEY, JSON.stringify({ ...draft, savedAt: now }));
    ok = ok || !!local;
  } catch {
    /* non-fatal */
  }
  return ok;
}

/**
 * Hand the shared page's pictures to the planner: the hero, and each stop's
 * photo under the key the planner's thumbnails look up (so "Lorne" keeps the
 * Lorne photo instead of drawing a placeholder).
 */
export function writeTakenPhotos(
  session: Pick<Storage, 'getItem' | 'setItem'> | null,
  { cover, stops }: { cover?: { url: string; name: string } | null; stops?: { name: string; url?: string | null }[] }
): void {
  if (!session) return;
  try {
    if (cover?.url) session.setItem(TAKEN_COVER_KEY, JSON.stringify(cover));
    else session.setItem(TAKEN_COVER_KEY, 'null');
  } catch {
    /* non-fatal */
  }
  const fresh = (stops || []).filter((s) => s.url && photoTitles(s.name)[0]);
  if (!fresh.length) return;
  try {
    const all = JSON.parse(session.getItem(STOP_PHOTO_STORE_KEY) || '{}') || {};
    fresh.forEach((s) => {
      const key = photoTitles(s.name)[0];
      if (!all[key]) all[key] = s.url;
    });
    session.setItem(STOP_PHOTO_STORE_KEY, JSON.stringify(all));
  } catch {
    /* non-fatal */
  }
}

/** True the first time this browser takes this token (then remembers it). */
export function firstTakeOf(token: string, local: Pick<Storage, 'getItem' | 'setItem'> | null): boolean {
  if (!token || !local) return false;
  try {
    const seen: string[] = JSON.parse(local.getItem(TAKEN_TOKENS_KEY) || '[]');
    if (Array.isArray(seen) && seen.includes(token)) return false;
    local.setItem(TAKEN_TOKENS_KEY, JSON.stringify([...(Array.isArray(seen) ? seen : []), token].slice(-50)));
    return true;
  } catch {
    return false;
  }
}

/** Remember which shared page the trip came from, for the way back. */
export function writeTakenFrom(session: Pick<Storage, 'setItem'> | null, token: string): void {
  if (!session || !/^[\w-]{4,128}$/.test(token)) return;
  try {
    session.setItem(TAKEN_FROM_KEY, token);
  } catch {
    /* non-fatal: the breadcrumb just won't link back */
  }
}

/** The shared page a taken trip came from, as a path, or null. Reads once (it clears). */
export function readTakenFrom(session: Pick<Storage, 'getItem' | 'removeItem'> | null): string | null {
  if (!session) return null;
  try {
    const token = session.getItem(TAKEN_FROM_KEY);
    session.removeItem(TAKEN_FROM_KEY);
    return token && /^[\w-]{4,128}$/.test(token) ? `/trip/shared/${token}` : null;
  } catch {
    return null;
  }
}

/* ── The road, handed over ─────────────────────────────────────────────────
 * The shared page has already asked Google for the road (the same call and
 * rounding the planner makes). Its per-leg figures and lines travel with the
 * trip, so the planner opens on the same 619 km and the same drawn route
 * instead of a straight-line guess that settles a second later.
 */
export const TAKEN_ROUTE_KEY = 'tp-taken-route';

type Pt = { lat: number; lng: number };

export interface TakenRoute {
  /** One per leg (stop i → i+1), Google's figures. */
  legs: { distanceKm: number; driveMinutes: number }[] | null;
  /** Each day's road, in its colour. */
  lines: { color: string; path: Pt[] }[];
  /** The shared page's map picture (same URL, so the browser already has it). */
  view?: TakenView | null;
}

export interface TakenView {
  lng: number;
  lat: number;
  zoom: number;
  width: number;
  height: number;
}

function cleanView(v: unknown): TakenView | null {
  const o = v as Partial<TakenView> | null;
  if (!o) return null;
  const nums = [o.lng, o.lat, o.zoom, o.width, o.height];
  if (!nums.every((n) => typeof n === 'number' && Number.isFinite(n))) return null;
  if ((o.width as number) <= 0 || (o.height as number) <= 0 || (o.width as number) > 2000 || (o.height as number) > 2000) return null;
  return { lng: o.lng as number, lat: o.lat as number, zoom: o.zoom as number, width: o.width as number, height: o.height as number };
}

/** Every k-th point (ends kept), so a long road fits in session storage. */
export function thinPath(path: Pt[], max = 240): Pt[] {
  if (path.length <= max) return path.map((p) => ({ lat: +p.lat.toFixed(5), lng: +p.lng.toFixed(5) }));
  const step = (path.length - 1) / (max - 1);
  const out: Pt[] = [];
  for (let i = 0; i < max; i++) {
    const p = path[Math.round(i * step)];
    out.push({ lat: +p.lat.toFixed(5), lng: +p.lng.toFixed(5) });
  }
  return out;
}

const goodFigure = (l: { distanceKm?: unknown; driveMinutes?: unknown }) =>
  typeof l.distanceKm === 'number' && Number.isFinite(l.distanceKm) && l.distanceKm >= 0 &&
  typeof l.driveMinutes === 'number' && Number.isFinite(l.driveMinutes) && l.driveMinutes >= 0;

export function writeTakenRoute(session: Pick<Storage, 'setItem' | 'removeItem'> | null, route: TakenRoute | null | undefined): void {
  if (!session) return;
  try {
    if (!route || (!route.legs?.length && !route.lines.length)) {
      session.removeItem(TAKEN_ROUTE_KEY);
      return;
    }
    const legs = route.legs && route.legs.every(goodFigure) ? route.legs : null;
    const lines = route.lines.filter((l) => l.path.length >= 2).map((l) => ({ color: l.color, path: thinPath(l.path) }));
    session.setItem(TAKEN_ROUTE_KEY, JSON.stringify({ legs, lines, view: cleanView(route.view) }));
  } catch {
    /* quota: the planner routes it itself, as before */
  }
}

/**
 * The handed-over road for a trip of `stopCount` stops, or null. Reads once
 * (it clears). Legs that don't match the stops are dropped; lines stay.
 */
export function readTakenRoute(session: Pick<Storage, 'getItem' | 'removeItem'> | null, stopCount: number): TakenRoute | null {
  if (!session) return null;
  try {
    const raw = session.getItem(TAKEN_ROUTE_KEY);
    session.removeItem(TAKEN_ROUTE_KEY);
    if (!raw) return null;
    const r = JSON.parse(raw);
    const legs = Array.isArray(r?.legs) && r.legs.length === stopCount - 1 && r.legs.every(goodFigure) ? r.legs : null;
    const lines = Array.isArray(r?.lines)
      ? r.lines
          .filter((l: { color?: unknown; path?: unknown }) => typeof l?.color === 'string' && /^#[0-9a-f]{3,8}$/i.test(l.color) && Array.isArray(l.path))
          .map((l: { color: string; path: Pt[] }) => ({
            color: l.color,
            path: l.path.filter((p) => typeof p?.lat === 'number' && typeof p?.lng === 'number'),
          }))
          .filter((l: { path: Pt[] }) => l.path.length >= 2)
      : [];
    return legs || lines.length ? { legs, lines, view: cleanView(r?.view) } : null;
  } catch {
    return null;
  }
}

/* ── Undo ──────────────────────────────────────────────────────────────────
 * Taking a trip replaces whatever unsaved trip the planner had. The old one
 * is kept aside, so "Undo" puts it back and returns to the shared page.
 */
export const TAKEN_PREV_KEY = 'tp-taken-prev';

export function stashPreviousDraft(
  session: Pick<Storage, 'getItem' | 'setItem'> | null,
  local: Pick<Storage, 'getItem'> | null
): void {
  if (!session) return;
  try {
    session.setItem(
      TAKEN_PREV_KEY,
      JSON.stringify({ session: session.getItem(TRIP_DRAFT_KEY), local: local ? local.getItem(TRIP_DRAFT_SHARED_KEY) : null })
    );
  } catch {
    /* non-fatal: Undo then just clears the copy */
  }
}

/** Put back the trip the take replaced (or clear the copy if there wasn't one). */
export function restorePreviousDraft(
  session: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null,
  local: Pick<Storage, 'setItem' | 'removeItem'> | null
): void {
  if (!session) return;
  let prev: { session?: string | null; local?: string | null } | null = null;
  try {
    prev = JSON.parse(session.getItem(TAKEN_PREV_KEY) || 'null');
  } catch {
    prev = null;
  }
  try {
    if (prev?.session) session.setItem(TRIP_DRAFT_KEY, prev.session);
    else session.removeItem(TRIP_DRAFT_KEY);
    session.removeItem(TAKEN_PREV_KEY);
  } catch {
    /* non-fatal */
  }
  try {
    if (prev?.local) local?.setItem(TRIP_DRAFT_SHARED_KEY, prev.local);
    else local?.removeItem(TRIP_DRAFT_SHARED_KEY);
  } catch {
    /* non-fatal */
  }
}

/** The path back to the shared page, without consuming it (Undo needs it later). */
export function peekTakenFrom(session: Pick<Storage, 'getItem'> | null): string | null {
  if (!session) return null;
  try {
    const token = session.getItem(TAKEN_FROM_KEY);
    return token && /^[\w-]{4,128}$/.test(token) ? `/trip/shared/${token}` : null;
  } catch {
    return null;
  }
}
